import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { APP_VERSION, BUILD_NUMBER } from '@/constants/version';

const TEST_UPDATE_BANNER = false;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const RELOAD_GUARD_KEY = 'version-check-reload-ts';
const RELOAD_GUARD_WINDOW_MS = 10_000;

interface IVersionCheckContext {
  newVersionAvailable: boolean;
  reloadPage: () => void;
}

const VersionCheckContext = React.createContext<IVersionCheckContext>({
  newVersionAvailable: false,
  reloadPage: () => {},
});

export const useVersionCheck = () => useContext(VersionCheckContext);

interface IDeployedVersion {
  version?: string;
  build?: string;
}

async function fetchDeployedVersion(): Promise<IDeployedVersion | null> {
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// Same-version redeploys differ only by build; compare it when both sides have one.
function isNewerDeploy(deployed: IDeployedVersion | null): boolean {
  if (!deployed?.version) return false;
  if (deployed.version !== APP_VERSION) return true;
  return !!deployed.build && !!BUILD_NUMBER && deployed.build !== BUILD_NUMBER;
}

function wasRecentlyReloaded(): boolean {
  const ts = sessionStorage.getItem(RELOAD_GUARD_KEY);
  if (!ts) return false;
  return Date.now() - Number(ts) < RELOAD_GUARD_WINDOW_MS;
}

function reloadPage() {
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
}

interface Props {
  children: React.ReactNode;
}

export const VersionCheckProvider: React.FC<Props> = ({ children }) => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const checkVersion = useCallback(async () => {
    if (isNewerDeploy(await fetchDeployedVersion())) {
      setNewVersionAvailable(true);
    }
  }, []);

  // Periodic polling + visibility-change check
  useEffect(() => {
    checkVersion();
    intervalRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkVersion]);

  // Catch resource load failures (stale CSS/JS 404s) and auto-reload
  useEffect(() => {
    const onResourceError = async (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const tag = target.tagName;
      if (tag !== 'SCRIPT' && tag !== 'LINK') return;

      if (wasRecentlyReloaded()) return;

      if (isNewerDeploy(await fetchDeployedVersion())) {
        reloadPage();
      }
    };

    window.addEventListener('error', onResourceError, true);
    return () => window.removeEventListener('error', onResourceError, true);
  }, []);

  return (
    <VersionCheckContext.Provider value={{ newVersionAvailable: newVersionAvailable || TEST_UPDATE_BANNER, reloadPage }}>
      {children}
    </VersionCheckContext.Provider>
  );
};
